/* eslint-disable @typescript-eslint/no-explicit-any */
import type { SDKError, Result as SDKResult } from '@speechifyinc/multiplatform-sdk';

type ResultError = any;

// Result type
type Result<T, E = ResultError> = { _ok: true; value: T } | { _ok: false; error: E };
interface Matchers<T, E extends ResultError, R1, R2> {
  ok: (value: T) => R1;
  err: (error: E) => R2;
}

// Result values
function Ok<T>(value: T): Result<T> {
  return { _ok: true, value };
}
function Err<T = any, E = Error>(error: E): Result<T, E> {
  return { _ok: false, error };
}

/**
 * takes a function that takes a ``fn(T)=> R``  returns a function ``fn(Result<T>) => Result<R>``
 *
 */
function wrap<T, R>(fn: (value: T) => R) {
  return (r: Result<T>): Result<R> => (r._ok === true ? { _ok: true, value: fn(r.value) } : r);
}

/**
 * Maps a Result<T, E> to a Result<V, E>
 */
function map<T, E, V>(r: Result<T, E>, fn: (value: T) => V): Result<V, E> {
  return r._ok === true ? Ok(fn(r.value)) : r;
}

/**
 * Maps a Result<T, E> to a Result<T, R>
 */
function mapErr<T, E, R>(r: Result<T, E>, fn: (value: E) => R) {
  return r._ok === false ? Err<T, R>(fn(r.error)) : r;
}

/**
 * Gives you T from Result<T, E> otherwise gives you the result of fn(E)
 */
function unwrapOr<T, E, F extends (value: E) => any>(r: Result<T, E>, fn: F): T | ReturnType<F> {
  return r._ok === true ? r.value : fn(r.error);
}

/**
 * Match value of result and perform apposite actions
 *
 * For example, if we want to redirect result to the correct console method
 * ```ts
 * match({
 *  ok: console.log,
 *  err: console.err
 * })(r)
 * ```
 */
function match<T, E, R1, R2>(matchers: Matchers<T, E, R1, R2>) {
  return (r: Result<T, E>) => (r._ok === true ? matchers.ok(r.value) : matchers.err(r.error));
}

function isOk<T>(r: Result<T>): r is { _ok: true; value: T } {
  return r._ok;
}

function isErr<T, E>(r: Result<T, E>): r is { _ok: false; error: E } {
  return !r._ok;
}

/**
 * Utility function to convert Result from SDK to Result type here
 */
async function fromSdkResult<T>(result: SDKResult<T>): Promise<Result<T, SDKError>> {
  const {
    default: { Result: SDKResult }
  } = await import('@speechifyinc/multiplatform-sdk');
  return result instanceof SDKResult.Success ? Ok(result.value) : result instanceof SDKResult.Failure ? Err(result.error) : Ok(undefined);
}

/**
 * Utility function to make migration to result type easier
 */

function resultify<F extends (...args: any[]) => any>(func: F): (...args: Parameters<F>) => Promise<Result<ReturnType<F>, Error>> {
  return async (...args) => {
    try {
      return Ok(await func(...args));
    } catch (e) {
      return Err(e);
    }
  };
}

function resultifySync<F extends (...args: any[]) => any>(func: F): (...args: Parameters<F>) => Result<ReturnType<F>, Error> {
  return (...args) => {
    try {
      return Ok(func(...args));
    } catch (e) {
      return Err(e);
    }
  };
}

export { Err, fromSdkResult, isErr, isOk, map, mapErr, match, Ok, resultify, resultifySync, unwrapOr, wrap };
export type { Result };
